import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// helpers
import { navigateBackOrToMain } from "../../../../common/utils/helpers/navigateBackOrToMain";
import { shouldShowMilesAndMore } from "../../../../common/utils/transport/airlineBookingPayload";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { frankfurtCardActionCreators } from "../frankfurtCardModule";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../components/ui/AppButton";
import MilesAndMoreInfo from "../../../../components/MilesAndMoreInfo";
import TransportCancellationPolicy from "../../../../components/TransportProviderContent/TransportCancellationPolicy";
import { type FrankfurtCardBookingForm, type FrankfurtCardOffer, type FrankfurtCardPassenger } from "../types";

interface FrankfurtCardSummaryProps {
  onBuyTicket: () => void;
  selectedOffer: FrankfurtCardOffer | null;
  cancellationPolicy?: string | null;
}

export default function FrankfurtCardSummary({
  onBuyTicket,
  selectedOffer,
  cancellationPolicy
}: FrankfurtCardSummaryProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("i18nextLng") || "en";

  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const prebookingInfoResponse = useSelector((state: any) => state.frankfurtCard.prebookingInfoResponse);
  const passengerList = useSelector((state: any) => state.frankfurtCard.passengerList) as FrankfurtCardPassenger[];
  const dataForBooking = useSelector((state: any) => state.frankfurtCard.dataForBooking) as FrankfurtCardBookingForm;
  const errorField = useSelector((state: any) => state.errorField.errorField) as any;

  const resetFrankfurtCardPrebookState = useActions(
    frankfurtCardActionCreators.resetFrankfurtCardPrebookStateAction,
    []
  );
  const updateBookingForm = useActions(frankfurtCardActionCreators.updateBookingFormAction, []);

  const isAirline = platformType === "airline";
  // Localised by the API for the selected date, e.g. "Valid on 23.10.2026 until 05:00 the following day."
  const validity: string | null = prebookingInfoResponse?.details?.validity ?? null;

  const handleBookingFormInputChange = (e: any, field: keyof FrankfurtCardBookingForm) => {
    updateBookingForm({
      ...dataForBooking,
      [field]: e.target.value
    });
  };

  const handleEditTicketOptionButton = () => {
    if (window.confirm(t("transport.editOptionText"))) {
      resetFrankfurtCardPrebookState();
    }
  };

  return (
    prebookingInfoResponse && (
      <Box sx={{ display: "flex", flexDirection: "column" }} pb={3} pt={3}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}>
            <Box>
              <Box className="summary-option-title" mb={2}>
                {t("transport.ticketOption")}
              </Box>
              <Box className="summary-option-title default-text-transform" data-cy="frankfurt-card-summary-option">
                {selectedOffer?.name}
                {validity ? ` - ${validity}` : ""}
              </Box>
            </Box>
            <Grid item sx={{ width: "auto", height: "40px" }} mt={{ xs: 1.5, sm: 0 }}>
              <AppButton
                label={t("transport.editBtn")}
                onClick={handleEditTicketOptionButton}
                disableRipple={true}
                customBackgroundColor={theme.palette.primary.dark}
                customHoverColor={theme.palette.primary.dark}
                fontSize="14px"
                dataAttribute="frankfurt-card-edit-option"
              />
            </Grid>
          </Box>

          <Grid container className="summary-passenger-box" mt={2} mb={2}>
            {passengerList?.length > 0 &&
              passengerList.map((passenger, index) => {
                const pricedPassenger = prebookingInfoResponse?.details?.passengers?.[index];

                return (
                  <Box
                    key={index}
                    className="summary-passenger-row"
                    data-cy={`frankfurt-card-summary-passenger-${index}`}
                  >
                    <Grid item xs={6} className="summary-passenger-cell" pl={{ xs: 1, sm: 0 }}>
                      {passenger?.firstName} {passenger?.lastName}
                    </Grid>
                    <Grid item xs={6} className="summary-passenger-cell">
                      {pricedPassenger?.price || ""} {pricedPassenger?.currency || ""}
                    </Grid>
                  </Box>
                );
              })}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center" }} />
            <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
              {t("transport.totalPrice")}: {prebookingInfoResponse?.formatted_price}
            </Box>
          </Box>

          <Box mt={2} className="frankfurt-card__note" data-cy="frankfurt-card-summary-no-refund-note">
            {t("transport.frankfurtCard.summaryNote")}
          </Box>
        </Box>

        <Grid container mt={4} spacing={2}>
          <Grid item xs={12} md={6}>
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestNameLabel")} *</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"nameForm"}
                  type="string"
                  value={dataForBooking?.nameForm}
                  onChange={event => handleBookingFormInputChange(event, "nameForm")}
                  error={errorField?.nameForm}
                  dataAttribute="frankfurt-card-name"
                />
              </Grid>
            </Grid>

            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestEmailLabel")} *</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"emailForm"}
                  type="string"
                  value={dataForBooking?.emailForm}
                  onChange={event => handleBookingFormInputChange(event, "emailForm")}
                  error={errorField?.emailForm}
                  dataAttribute="frankfurt-card-email"
                />
              </Grid>
            </Grid>

            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.commentLabel")}</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"commentForm"}
                  type="string"
                  value={dataForBooking?.commentForm}
                  onChange={event => handleBookingFormInputChange(event, "commentForm")}
                  error={errorField?.commentForm}
                  dataAttribute="frankfurt-card-comment"
                />
              </Grid>
            </Grid>
          </Grid>

          {shouldShowMilesAndMore(prebookingInfoResponse?.miles_and_more, isAirline) && (
            <Grid item xs={12} md={6} pl={{ xs: 0, md: 5 }} pt={{ xs: 2, md: 0 }}>
              <MilesAndMoreInfo miles={prebookingInfoResponse?.miles_and_more} />
            </Grid>
          )}
        </Grid>

        {cancellationPolicy && (
          <Box pt={6} data-cy="frankfurt-card-summary-cancellation-policy">
            <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
          </Box>
        )}

        <Grid
          item
          xs={12}
          sx={{
            width: { xs: "100%", sm: "100%", md: isAirline ? "45%" : "30%" },
            margin: cancellationPolicy ? "24px auto 0 auto" : "60px auto 0 auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 2 }
          }}
        >
          {isAirline && (
            <AppButton
              label={t("transport.goBack")}
              onClick={() => navigateBackOrToMain(navigate, { language })}
              disableRipple={true}
              customBackgroundColor={theme.palette.primary.dark}
              customHoverColor={theme.palette.primary.dark}
              fontSize="14px"
              dataAttribute="frankfurt-card-go-back-2"
            />
          )}
          <AppButton
            label={t("transport.buyTicketsBtn")}
            onClick={onBuyTicket}
            disableRipple={true}
            customBackgroundColor={tenantInfo?.style?.button?.color}
            customActiveColor={tenantInfo?.style?.button?.active}
            customHoverColor={tenantInfo?.style?.button?.hover}
            customClickColor={tenantInfo?.style?.button?.click}
            fontSize="14px"
            dataAttribute="frankfurt-card-buy-tickets"
          />
        </Grid>
      </Box>
    )
  );
}
